/**
 * Shared Cloudinary utilities for URL transformation.
 *
 * Used by both frontend and backend to ensure consistent image optimization.
 */

/** The Viewrail Cloudinary CDN hostname */
export const CLOUDINARY_HOSTNAME = 'https://assets.viewrail.com/';

/** Image width for product feed (Google Merchant Center recommends minimum 800px, ideally 1500px+) */
export const PRODUCT_FEED_IMAGE_WIDTH = 1500;

/** Image width for email templates (optimized for email client compatibility and load times) */
export const EMAIL_IMAGE_WIDTH = 500;

/** Transform options for Studio data source preview thumbnails (small resized previews) */
export const STUDIO_PREVIEW_OPTIONS: CloudinaryTransformOptions = {
  width: 200,
  crop: 'fill',
};

/**
 * Checks if a URL is from the Viewrail Cloudinary CDN.
 */
export function isCloudinaryUrl(url: string | undefined): url is string {
  return Boolean(url && url.startsWith(CLOUDINARY_HOSTNAME));
}

/**
 * Injects transformation parameters into a Cloudinary URL.
 *
 * Transformations are inserted after `/upload/`, preserving any version number.
 * Returns the URL unchanged if it doesn't contain `/upload/`.
 *
 * Note: This function does not validate the URL. Use `transformCloudinaryUrl`
 * for safe transformation that checks the URL origin first.
 *
 * @example
 * // Input:  https://assets.viewrail.com/image/upload/v123/image.jpg
 * // Output: https://assets.viewrail.com/image/upload/w_500,c_limit/v123/image.jpg
 */
export function injectCloudinaryTransformations(
  url: string,
  transformations: string,
): string {
  return url.replace(/\/upload\//, `/upload/${transformations}/`);
}

export interface CloudinaryTransformOptions {
  /** Image width in pixels (e.g. `500`) */
  width?: number;
  /** Image height in pixels (e.g. `600`) */
  height?: number;
  /** Cloudinary quality value without prefix (e.g. `'auto'`, `'80'`) */
  quality?: string;
  /** Cloudinary crop mode without prefix (e.g. `'limit'`, `'fill'`) */
  crop?: string;
  /** Cloudinary format without prefix (e.g. `'auto'`, `'webp'`) */
  format?: string;
  /** Cloudinary gravity without prefix (e.g. `'auto'`, `'face'`, `'custom'`) */
  gravity?: string;
  /** X coordinate for custom gravity */
  x?: string;
  /** Y coordinate for custom gravity */
  y?: string;
  /** Aspect ratio (e.g. `'1:1'`, `'16:9'`) */
  aspectRatio?: string;
  /** Prefixed effect strings applied as a chained transformation (e.g. `['e_brightness:-3']`) */
  effects?: readonly string[];
}

/**
 * Builds a transformation string from options.
 *
 * @example
 * buildTransformationString({ width: 500 })
 * // Returns: "w_500,c_limit,q_auto,f_auto"
 */
export function buildTransformationString(options: CloudinaryTransformOptions): string {
  const { width, height, quality = 'auto', crop = 'limit', format = 'auto', gravity, x, y, aspectRatio, effects } =
    options;

  const parts = [
    width && `w_${width}`,
    height && `h_${height}`,
    aspectRatio && `ar_${aspectRatio}`,
    `c_${crop}`,
    gravity && `g_${gravity}`,
    gravity === 'custom' && x && `x_${x}`,
    gravity === 'custom' && y && `y_${y}`,
    `q_${quality}`,
    `f_${format}`,
  ]
    .filter(Boolean)
    .join(',');

  return effects?.length ? parts + '/' + effects.join(',') : parts;
}

/**
 * Transforms a Cloudinary URL with the given options.
 *
 * Returns undefined if the input is undefined.
 * Returns the original URL unchanged if it's not from the Viewrail Cloudinary CDN.
 *
 * @example
 * transformCloudinaryUrl('https://assets.viewrail.com/image/upload/v123/img.jpg', { width: 500 })
 * // Returns: 'https://assets.viewrail.com/image/upload/w_500,c_limit,q_auto,f_auto/v123/img.jpg'
 *
 * transformCloudinaryUrl('https://other-cdn.com/img.jpg', { width: 500 })
 * // Returns: 'https://other-cdn.com/img.jpg' (unchanged)
 */
export function transformCloudinaryUrl(
  url: string | undefined,
  options: CloudinaryTransformOptions,
): string | undefined {
  if (!isCloudinaryUrl(url)) {
    return url;
  }

  const transformations = buildTransformationString(options);
  return injectCloudinaryTransformations(url, transformations);
}
